import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { MapPinHouse } from "lucide-react";
import { toneClasses } from "@/components/ui/badgeTones";
import {
  formatDistance,
  shortenDistrict,
} from "@/features/orders/lib/locality";
import { cn } from "@/lib/cn";
import type { LocalityRef, LocalityStatus } from "@/types/localities";

interface Props {
  locality: LocalityRef | null;
  /** Already normalised; `manual` with a null locality is treated as `not_found`. */
  status: LocalityStatus;
  distanceM: number | null;
  /** Dispatcher / company admin only — every action is hidden otherwise. */
  canEdit: boolean;
  onEdit: () => void;
  /** Ambiguous only: turn the nearest candidate into a manual choice. */
  onConfirmAmbiguous: () => void;
  confirmPending: boolean;
}

/** Text-button styling shared by «Изменить» and «Подтвердить …» (spec §2.3). */
const TEXT_BUTTON =
  "min-h-[32px] rounded px-1 text-xs font-semibold text-brand-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 aria-disabled:cursor-not-allowed aria-disabled:opacity-60 aria-disabled:hover:no-underline";

const MARKER =
  "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold";

/**
 * «НП: Новосёлки, Минский р-н» — the locality line on the order and permanent
 * order cards (Sprint 228, TASK-5174, design spec §2.3).
 *
 * One visual row (icon, text, optional marker pill, action) plus an optional
 * hint. The marker is real text, read in order by a screen reader («НП:
 * Новосёлки, Минский р-н, вручную»); there is no live region here — changes are
 * announced by the toast.
 *
 * The edit button's ref is forwarded so `LocalitySection` can return focus to
 * it when the picker closes.
 */
export const LocalityLine = forwardRef<HTMLButtonElement, Props>(
  function LocalityLine(
    {
      locality,
      status,
      distanceM,
      canEdit,
      onEdit,
      onConfirmAmbiguous,
      confirmPending,
    },
    editButtonRef,
  ) {
    const { t, i18n } = useTranslation();

    // Defensive: a `manual` (or `auto`/`ambiguous`) row whose locality is gone
    // reads as `not_found` rather than an empty name.
    const effective: LocalityStatus =
      status !== "" && status !== "not_found" && locality === null
        ? "not_found"
        : status;
    const isProblem = effective === "ambiguous" || effective === "not_found";

    const actionLabel =
      effective === "ambiguous"
        ? t("orders.locality.pickOther")
        : effective === "not_found" || effective === ""
          ? t("orders.locality.set")
          : t("orders.locality.change");

    const shown = locality && effective !== "not_found" ? locality : null;
    // Its own span so «р-н» never breaks as «р-» / «н» (Sprint 228 UI review).
    const district = shown ? shortenDistrict(shown.district) : null;
    // «?» plus its spoken form, so «Новосёлки вопрос» is never the message.
    const ambiguousMark =
      effective === "ambiguous" ? (
        <>
          ?<span className="sr-only"> {t("orders.locality.ambiguousSr")}</span>
        </>
      ) : null;
    const prefix = t("orders.locality.prefix");
    const full = t("orders.locality.full");

    return (
      <div data-testid="locality-line" data-status={effective}>
        <div className="flex items-start gap-2">
          <MapPinHouse
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0",
              isProblem ? "text-brand-warning" : "text-gray-400",
            )}
            aria-hidden="true"
          />
          <p className="min-w-0 flex-1 break-words text-sm text-gray-700">
            {/* An abbreviation only where there is one to expand (ru «НП» →
                «Населённый пункт»); in en both read «Locality». */}
            {prefix !== full ? (
              <abbr title={full} className="no-underline">
                {prefix}
              </abbr>
            ) : (
              prefix
            )}
            :{" "}
            {shown ? (
              <span className="font-medium text-gray-900">
                {shown.name}
                {district ? (
                  <>
                    ,{" "}
                    {/* The ambiguous «?» rides INSIDE the no-wrap span, so it
                        never wraps onto a line of its own after the district
                        («Барановичский р-н» / «?» at 1024px). */}
                    <span
                      className="whitespace-nowrap"
                      data-testid="locality-district"
                    >
                      {district}
                      {ambiguousMark}
                    </span>
                  </>
                ) : (
                  ambiguousMark
                )}
              </span>
            ) : (
              <span>{t("orders.locality.none")}</span>
            )}
            {effective === "manual" && (
              <>
                {" "}
                <span className={cn(MARKER, toneClasses("neutral"))}>
                  {t("orders.locality.marker.manual")}
                </span>
              </>
            )}
            {effective === "ambiguous" && (
              <>
                {" "}
                <span
                  className={cn(MARKER, toneClasses("warning"))}
                  aria-hidden="true"
                >
                  {t("orders.locality.marker.ambiguous")}
                </span>
              </>
            )}
            {effective === "not_found" && (
              <>
                {" "}
                <span className={cn(MARKER, toneClasses("warning"))}>
                  {t("orders.locality.marker.notFound")}
                </span>
              </>
            )}
            {effective === "" && (
              <>
                {" "}
                <span className={cn(MARKER, toneClasses("neutral"))}>
                  {t("orders.locality.marker.unresolved")}
                </span>
              </>
            )}
          </p>
          {canEdit && (
            <button
              ref={editButtonRef}
              type="button"
              onClick={onEdit}
              aria-label={t("orders.locality.changeAria")}
              className={cn(TEXT_BUTTON, "shrink-0")}
            >
              {actionLabel}
            </button>
          )}
        </div>

        {effective === "ambiguous" && (
          <div className="ml-6 mt-0.5 space-y-0.5">
            <p className="text-xs text-gray-500">
              {t("orders.locality.ambiguousHint", {
                distance:
                  distanceM !== null
                    ? formatDistance(distanceM, i18n.language, {
                        m: t("orders.locality.unitM"),
                        km: t("orders.locality.unitKm"),
                      })
                    : "—",
              })}
            </p>
            {canEdit && locality && (
              <button
                type="button"
                // `aria-disabled`, not `disabled`: disabling the clicked
                // button would drop focus to <body> mid-save (code review 6).
                onClick={() => {
                  if (!confirmPending) onConfirmAmbiguous();
                }}
                aria-disabled={confirmPending || undefined}
                className={cn(TEXT_BUTTON, "-ml-1")}
              >
                {confirmPending
                  ? t("orders.locality.saving")
                  : t("orders.locality.confirm", { name: locality.name })}
              </button>
            )}
          </div>
        )}

        {effective === "not_found" && (
          <p className="ml-6 mt-0.5 text-xs text-gray-500">
            {t("orders.locality.notFoundHint")}
          </p>
        )}
      </div>
    );
  },
);
